import type { TrialRecap } from '@/hooks/useTrialRecap';
import { Button } from '@/components/ui/button';
import { Wallet } from 'lucide-react';

interface Props {
  recap: TrialRecap | null;
  onContinue: () => void;
}

function formatMoney(amount: number, currency: string) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(0)}`;
  }
}

export function PageMoneyTracked({ recap, onContinue }: Props) {
  const total = recap?.money.totalLogged ?? 0;
  const currency = recap?.currency ?? 'USD';
  const count = recap?.money.expenseCount ?? 0;
  const top = recap?.money.topCategory;
  const adherence = recap?.money.budgetAdherence;

  return (
    <div className="min-h-full flex flex-col px-6 py-12 max-w-lg mx-auto">
      <p className="text-[10px] uppercase tracking-[0.3em] text-primary/70 mb-3">
        Chapter two
      </p>
      <h1 className="text-3xl sm:text-4xl font-display font-semibold tracking-tight mb-6">
        I finally saw<br />where my money went.
      </h1>

      <div className="flex items-center gap-4 mb-8">
        <div className="h-14 w-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
          <Wallet className="h-6 w-6 text-primary" />
        </div>
        <div>
          <p className="text-4xl font-bold tracking-tight">
            {formatMoney(total, currency)}
          </p>
          <p className="text-xs text-muted-foreground/70 uppercase tracking-widest">
            tracked across {count} entries
          </p>
        </div>
      </div>

      {top && (
        <div className="rounded-2xl border border-border/30 bg-muted/5 p-5 mb-4">
          <p className="text-xs text-muted-foreground/70 uppercase tracking-widest mb-1">
            Top category
          </p>
          <p className="text-lg font-semibold">{top.name}</p>
          <p className="text-sm text-muted-foreground/70">
            {formatMoney(top.amount, currency)} - {top.percent}% of my spend
          </p>
        </div>
      )}

      {adherence && (
        <div className="rounded-2xl border border-border/30 bg-muted/5 p-5 mb-6">
          <p className="text-xs text-muted-foreground/70 uppercase tracking-widest mb-1">
            Budget adherence
          </p>
          <p className="text-lg font-semibold">
            {adherence.daysUnder} of {adherence.daysTotal} days under budget
          </p>
        </div>
      )}

      <p className="text-xs text-muted-foreground/60 leading-relaxed mb-8">
        On free, my budget history stays visible for 30 days. Everything
        beyond that fades.
      </p>

      <div className="mt-auto pt-6">
        <Button onClick={onContinue} className="w-full" size="lg">
          Continue
        </Button>
      </div>
    </div>
  );
}
